// Who is signed in: GET /me gates the whole app.

import { type MeResponse, meResponseSchema } from '@enp/domain/api';
import { queryOptions, type QueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api.js';
import type { AuthProvider } from './providers.js';
import { SignInError, type SignInInput } from './providers.js';
import { getRefreshToken, getToken, setSession, setToken } from './token-store.js';

/** Shape of `GET /me`: the shared contract in @enp/domain/api. */
export type Me = MeResponse;

const fetchMe = (request: { signal?: AbortSignal; token?: string }) =>
  api('/me', { ...request, schema: meResponseSchema });

export const meQueryOptions = () =>
  queryOptions({
    queryKey: ['me'],
    // Never cancelled: the app's gate (the `_app` route's beforeLoad) waits on this fetch. With
    // the signal passed, React Query cancelled it whenever the last component reading it
    // unmounted (the loading frame swapping, React's dev double mount), and a /me slower
    // than about a second ended as "This page didn't load" (ref CancelledError).
    queryFn: () => fetchMe({}),
    staleTime: 5 * 60_000,
    retry: false,
  });

/** Signs in, proves the token against /me, then stores it. */
export async function signIn(
  auth: AuthProvider,
  queryClient: QueryClient,
  input: SignInInput,
): Promise<Me> {
  const session = await auth.signIn(input);
  let me: Me;
  try {
    me = await fetchMe({ token: session.token });
  } catch (error) {
    if (error instanceof ApiError && error.status === 403) {
      throw new SignInError(
        'This email isn’t part of any account yet. Check the spelling, or ask the owner to add you.',
      );
    }
    if (error instanceof ApiError && error.status === 401) {
      throw new SignInError('The server didn’t accept this session. Try signing in again.');
    }
    throw error;
  }
  setSession(session);
  queryClient.setQueryData(meQueryOptions().queryKey, me);
  return me;
}

export async function signOut(auth: AuthProvider, queryClient: QueryClient): Promise<void> {
  const token = getToken();
  try {
    await auth.signOut(token ? { token, refreshToken: getRefreshToken() } : null);
  } finally {
    setToken(null);
    queryClient.clear();
  }
}

export const isSignedIn = () => getToken() !== null;
